import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { closeCart, resetCheckout, setCheckoutStep } from "@store/reducers/cart";

import { ERoutes } from "@utils/constants";
import styled, { keyframes } from "styled-components";

import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";

import {
  Body,
  BottomFade,
  DesktopHeader,
  FirstSectionLabel,
  PhoneHeader,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "./phoneTemplate";
import {
  BackBtn,
  ButtonGroup,
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  IconCircle,
  PrimaryBtn,
  SectionLabel,
  SlidePanelFooter,
  StepSubtitle,
  StepTitle,
} from "./styles";

// Same inline spinner used in SubPaymentView / UpgradePaymentView so the
// processing state reads identical across all checkout buttons.
const spin = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled.span`
  display: inline-block; width: 14px; height: 14px; margin-right: 8px;
  /* flex:0 0 auto so the narrow button never compresses the circle into an oval. */
  flex: 0 0 auto; box-sizing: border-box;
  border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%;
  animation: ${spin} 0.7s linear infinite; vertical-align: -2px;
`;

/* Footer: Back + Pay. On phone, Back shrinks to its content width and Pay flexes
   to fill, so the "Pay $X Now" label fits on ONE line (a 50/50 split wrapped it
   to two lines, then it collapsed to one when the busy "Processing…" replaced
   it on click). nowrap is belt-and-suspenders against a long amount. */
const Actions = styled(ButtonGroup)`
  margin-top: 0;
  border-top: none;
  padding-top: 0;

  @media (max-width: 600px) {
    ${BackBtn} {
      flex: 0 0 auto;
      width: auto;
      padding-left: 20px;
      padding-right: 20px;
    }
    ${PrimaryBtn} {
      flex: 1;
      width: auto;
      white-space: nowrap;
    }
  }
`;

/** Poll the upgrade-order endpoint until the `invoice.paid` webhook has built
 *  the order behind the scenes (or we give up). Returns the order, or null on
 *  timeout — the caller then lands on the "being processed" page while the
 *  reconcile backstop finishes it. The wait happens on the Pay button. */
const pollUpgradeOrder = async (invoiceId: string): Promise<any | null> => {
  const started = Date.now();
  const MAX_WAIT_MS = 30000;
  while (Date.now() - started < MAX_WAIT_MS) {
    try {
      const r = await instance.get(`/stripe/subscriptions/upgrade-order/${encodeURIComponent(invoiceId)}`);
      if (r.data && (r.data.id || r.data._id)) return r.data;
    } catch { /* keep waiting */ }
    await new Promise((res) => setTimeout(res, 1500));
  }
  return null;
};

/** Fetch an already-built order (the inline $0 / dev path). */
const fetchOrder = async (id: string): Promise<any | null> => {
  try {
    const r = await instance.get(`/orders/${id}`);
    return r.data || null;
  } catch {
    return null;
  }
};

interface PaymentMethodSummary {
  type: string;
  last4: string;
  brand: string;
  expMonth: number | null;
  expYear: number | null;
  wallet: string | null;
}

const UpgradeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const DowngradeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="rgb(97, 84, 73)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const consentShake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? consentShake : "none")} 0.5s ease;
`;

const UpgradeReviewView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const subConfig = useSubscriptionConfig();
  const { subscriptionUpgrade } = useAppSelector((state) => state.cart);
  const [submitting, setSubmitting] = useState(false);
  // Second spinner stage — flips on once the backend change call resolves and
  // we're applying it / navigating, so the button reads "Finalizing..." like
  // the cart's Pay button instead of sitting on "Processing...".
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable per-attempt idempotency key — generated ONCE per view mount (the
  // upgrade config is fixed per mount) and reused across Pay re-clicks, so a
  // re-click after a lost response can't double-charge (the backend looks up the
  // prior attempt by this key and returns its result instead of charging again).
  const idemKeyRef = useRef<string>("");
  // Auto-scroll the error into view — it renders at the bottom of the scroll
  // body, so otherwise the user wouldn't see it below the fold.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [error]);
  // Scroll affordance — show the "Scroll ⌄" cue while there's more below the
  // fold (this body can overflow on small phones).
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);
  // True when the error is a payment-card problem (declined / detached / expired
  // card) — we then point the user at the card row above instead of leaking the
  // raw Stripe message, and hide the redundant inline "Change card" tail.
  // Consent for THIS purchase. An upgrade or add-on is a new contract with its
  // own 14-day withdrawal period, so the waiver given at signup does not cover
  // it — it has to be captured again here. A DOWNGRADE is not a purchase (no
  // charge, no new entitlement), so it asks for nothing.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [consentAttempted, setConsentAttempted] = useState(false);
  const [shakeTerms, setShakeTerms] = useState(false);
  const [shakeWaiver, setShakeWaiver] = useState(false);
  const [cardError, setCardError] = useState(false);
  // True when the error is an expired session (401) — show a log-in prompt and
  // hide the "Change card" affordance (it's not a payment problem).
  const [authError, setAuthError] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodSummary | null>(null);

  // Fetch the saved payment method that the upgrade will charge. When the
  // user updates their card via the modal, refetch so the row reflects the
  // new card on file (and Stripe will auto-charge that one on Pay).
  const loadPaymentMethod = useCallback(async () => {
    try {
      const resp = await instance.get("/stripe/payment-method");
      setPaymentMethod(resp.data || null);
    } catch {
      setPaymentMethod(null);
    }
  }, []);
  useEffect(() => { loadPaymentMethod(); }, [loadPaymentMethod]);

  if (!subscriptionUpgrade) return null;

  const { oldChannels, oldClaims, newChannels, newClaims, billingPeriod, oldTotal, newTotal, daysLeftInPeriod, cycleLengthDays, periodEndDate, isDowngrade } = subscriptionUpgrade;
  const isAnnual = billingPeriod === "annual";
  const hasChanges = newChannels !== oldChannels || newClaims !== oldClaims;

  // Pricing math:
  // - Monthly upgrade = cycle-reset: charge newMonthly − (oldMonthly × daysLeft/30), new 30-day cycle starts today
  // - Annual  upgrade = prorated rest-of-year diff, year continues unchanged
  const fmtLong = (d: Date) => d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const today = new Date();
  const monthlyNextRenewal = new Date(today);
  monthlyNextRenewal.setDate(monthlyNextRenewal.getDate() + 30);

  // Credit denominator = actual cycle length (period_end − period_start),
  // not a hardcoded 30. Matches the backend `buildSubscriptionLineItems`
  // calc so the preview math equals what's charged.
  const cashCreditMonthly = Math.round(oldTotal * (Math.max(0, daysLeftInPeriod) / Math.max(1, cycleLengthDays ?? 30)) * 100) / 100;
  const chargeTodayMonthly = Math.max(0, Math.round((newTotal - cashCreditMonthly) * 100) / 100);

  const yearlyDiff = (newTotal - oldTotal) * 12;
  const proratedDiffAnnual = Math.round(yearlyDiff * (Math.max(0, daysLeftInPeriod) / 365) * 100) / 100;

  return (
    <>
      {/* PHONE: fixed header — centred title, no icon (✕ stays top-right). */}
      <PhoneHeader>
        <h2>{isDowngrade ? "Review Downgrade" : "Review Upgrade"}</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
      {/* DESKTOP/TABLET: original centred icon-circle header (hidden on phone). */}
      <DesktopHeader>
        <IconCircle>{isDowngrade ? <DowngradeIcon /> : <UpgradeIcon />}</IconCircle>
        <div>
          <StepTitle>{isDowngrade ? "Review Downgrade" : "Review Upgrade"}</StepTitle>
          <StepSubtitle>Review your configuration changes before confirming</StepSubtitle>
        </div>
      </DesktopHeader>

      {/* Card 1: Configuration + new recurring price */}
      <FirstSectionLabel><span>{hasChanges ? "Your New Configuration" : "Your Configuration"}</span></FirstSectionLabel>
      <div style={{
        marginBottom: 12,
        background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(235, 194, 142, 0.25)",
        borderRadius: "12px", padding: "14px 18px 18px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
        fontSize: "0.875rem",
        overflow: "hidden",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#fff" }}>
          <span>Channels</span>
          <span style={hasChanges && newChannels !== oldChannels ? { fontWeight: 600 } : undefined}>{newChannels}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", color: "#fff" }}>
          <span>Claims{newChannels > 1 ? " per channel" : ""}</span>
          <span style={hasChanges && newClaims !== oldClaims ? { fontWeight: 600 } : undefined}>{newClaims}</span>
        </div>
        <div style={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)", paddingTop: "12px", marginTop: "12px", display: "flex", justifyContent: "space-between", color: "#fff" }}>
          <span>
            {isDowngrade
              ? <>New monthly rate {isAnnual && <span style={{ color: "rgba(251, 244, 236, 0.55)", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif" }}>(−{subConfig.annualDiscountPercent}%)</span>}</>
              : isAnnual
                ? `Yearly Rate from ${periodEndDate ? fmtLong(new Date(periodEndDate)) : "next renewal"}`
                : `Monthly Rate from ${fmtLong(monthlyNextRenewal)}`}
          </span>
          <span style={{ fontWeight: 600 }}>
            {isDowngrade
              ? `$${newTotal.toFixed(2)}/mo`
              : isAnnual
                ? <>${(Math.round(newTotal * 12 * 100) / 100).toFixed(2)}/year <span style={{ fontWeight: 400, color: "#fff" }}>(${newTotal.toFixed(2)}/mo)</span></>
                : `$${newTotal.toFixed(2)}/mo`}
          </span>
        </div>
      </div>

      {/* Card 2: First-month / today's charge — prominent, only for upgrades */}
      {!isDowngrade && hasChanges && (() => {
        const today = isAnnual ? proratedDiffAnnual : chargeTodayMonthly;
        return (
          <div style={{
            marginBottom: 20,
            background: "rgba(156, 211, 253, 0.08)",
            border: "1px solid rgba(156, 211, 253, 0.4)",
            borderRadius: "12px", padding: "16px 18px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            overflow: "hidden",
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.75rem", color: "rgba(156, 211, 253, 0.95)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Charged Today
              </div>
              {!isAnnual && cashCreditMonthly > 0 && (
                <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif", fontSize: "0.875rem", color: "#fff", marginTop: 4 }}>
                  −${cashCreditMonthly.toFixed(2)} for {daysLeftInPeriod} unused day{daysLeftInPeriod === 1 ? "" : "s"} of your current plan
                </div>
              )}
              {isAnnual && (
                <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.75rem", color: "rgba(251, 244, 236, 0.65)", marginTop: 4 }}>
                  Prorated through {periodEndDate ? fmtLong(new Date(periodEndDate)) : "next renewal"}
                </div>
              )}
            </div>
            <div style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
              fontSize: "1.75rem", fontWeight: 700, color: "#9cd3fd", lineHeight: 1, whiteSpace: "nowrap",
            }}>
              ${today.toFixed(2)}
            </div>
          </div>
        );
      })()}

      {/* Downgrade comparison card — same blue frame as the upgrade
          Charged Today card, but shows the price CHANGE (current → new)
          instead of an amount to charge. Downgrades are deferred to the
          next renewal so no money moves today. */}
      {isDowngrade && (() => {
        const newYearly = Math.round(newTotal * 12 * 100) / 100;
        const monthlySavings = Math.round((oldTotal - newTotal) * 100) / 100;
        return (
          <div style={{
            marginBottom: 20,
            background: "rgba(156, 211, 253, 0.08)",
            border: "1px solid rgba(156, 211, 253, 0.4)",
            borderRadius: "12px", padding: "16px 18px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
            overflow: "hidden",
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: "0.75rem", color: "rgba(156, 211, 253, 0.95)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                New {isAnnual ? "Annual" : "Monthly"} Payment
                {isAnnual && <span style={{ color: "rgba(251, 244, 236, 0.55)", marginLeft: 6, fontSize: "0.875rem", letterSpacing: "normal", textTransform: "none", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif" }}>(−{subConfig.annualDiscountPercent}%)</span>}
              </div>
              <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif", fontSize: "0.875rem", color: "#fff", marginTop: 4 }}>
                From {periodEndDate ? fmtLong(new Date(periodEndDate)) : (isAnnual ? "your next annual renewal" : "your next billing date")}
                {monthlySavings > 0 && (
                  <> · You save <strong>${monthlySavings.toFixed(2)}/mo</strong></>
                )}
              </div>
            </div>
            <div style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
              fontSize: "1.75rem", fontWeight: 700, color: "#9cd3fd", lineHeight: 1, whiteSpace: "nowrap",
              flexShrink: 0,
            }}>
              ${isAnnual ? newYearly.toFixed(2) : newTotal.toFixed(2)}<span style={{ fontSize: "0.85em", fontWeight: 400 }}>/{isAnnual ? "year" : "mo"}</span>
            </div>
          </div>
        );
      })()}

      {/* Payment Method row — show the card Stripe will auto-charge, plus a
          Change link that opens the UpdatePaymentMethodModal. After the user
          updates, the row refetches and shows the new card. Hidden when no
          PM is on file (shouldn't happen for an existing subscriber, but
          guarded anyway). Hidden on downgrades since there's no charge. */}
      {!isDowngrade && paymentMethod && (
        <>
          <SectionLabel><span>Payment Method</span></SectionLabel>
          <div style={{
            marginBottom: 20,
            background: "rgba(255, 255, 255, 0.04)",
            border: "1px solid rgba(235, 194, 142, 0.25)",
            borderRadius: "12px",
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif", fontSize: "0.9375rem", color: "#fff" }}>
                <span style={{ textTransform: "capitalize" }}>{paymentMethod.brand}</span>
                {" •••• "}
                {paymentMethod.last4}
                {paymentMethod.expMonth != null && paymentMethod.expYear != null && (
                  <span style={{ marginLeft: 10, color: "rgba(251, 244, 236, 0.5)", fontSize: "0.8125rem" }}>
                    {String(paymentMethod.expMonth).padStart(2, "0")}/{String(paymentMethod.expYear).slice(-2)}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => dispatch(setCheckoutStep("upgrade-payment"))}
              style={{
                background: "transparent",
                border: "none",
                padding: "4px 8px",
                color: "#9cd3fd",
                fontFamily: "'Raleway', sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                textDecoration: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
            >
              Change card
            </button>
          </div>
        </>
      )}

      {error && (
        <div ref={errorRef} style={{
          padding: "10px 14px",
          borderRadius: "8px",
          background: "rgba(228, 122, 106, 0.12)",
          border: "1px solid rgba(228, 122, 106, 0.3)",
          color: "#e47a6a",
          fontSize: "0.875rem",
          fontFamily: "'Raleway', sans-serif",
          marginBottom: "16px",
          lineHeight: 1.5,
        }}>
          {error}
          {!isDowngrade && !cardError && !authError && (
            <>
              {" "}
              <button
                type="button"
                onClick={() => { setError(null); setCardError(false); setAuthError(false); dispatch(setCheckoutStep("upgrade-payment")); }}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "#9cd3fd",
                  fontFamily: "'Raleway', sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Change card
              </button>
              {" and try again."}
            </>
          )}
        </div>
      )}
      {/* Consent — upgrades/add-ons only. This is a fresh contract at a new
          price, so §18/§16 FAGG want the acceptance captured for THIS purchase
          rather than inherited from signup. A downgrade charges nothing and
          grants nothing, so it is deliberately exempt. */}
      {!isDowngrade && hasChanges && (
        <>
          <SectionLabel><span>Terms &amp; Conditions</span></SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "8px" }}>
            <ShakeWrap $shake={shakeTerms}>
              <CheckboxRow
                onClick={() => setTermsAccepted((v) => !v)}
                style={{ cursor: "pointer", border: `1px solid ${consentAttempted && !termsAccepted ? "#e47a6a" : "transparent"}` }}
              >
                <CheckboxBox $checked={termsAccepted}>{termsAccepted && <CheckIcon />}</CheckboxBox>
                <CheckboxLabel>
                  I agree to the{" "}
                  <a href={ERoutes.Conditions} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#ebc28e" }}>Terms of Use</a>,{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#ebc28e" }}>Privacy Policy</a>,{" "}
                  <a href={ERoutes.LicenceTerms} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#ebc28e" }}>YouTube Licence Terms</a>, and{" "}
                  <a href={ERoutes.RefundPolicy} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#ebc28e" }}>Refund Policy</a>.
                </CheckboxLabel>
              </CheckboxRow>
            </ShakeWrap>

            <ShakeWrap $shake={shakeWaiver}>
              <CheckboxRow
                onClick={() => setWaiverAccepted((v) => !v)}
                style={{ cursor: "pointer", border: `1px solid ${consentAttempted && !waiverAccepted ? "#e47a6a" : "transparent"}` }}
              >
                <CheckboxBox $checked={waiverAccepted}>{waiverAccepted && <CheckIcon />}</CheckboxBox>
                <CheckboxLabel>
                  I want immediate access to this change and acknowledge that I waive my 14-day right of withdrawal under EU consumer law.
                </CheckboxLabel>
              </CheckboxRow>
            </ShakeWrap>
          </div>
        </>
      )}

      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <Actions>
          <BackBtn
            type="button"
            disabled={submitting}
            onClick={() => {
              if (submitting) return;
              // Reopen the manage subscription modal once the cart slide-out is closed.
              window.dispatchEvent(new CustomEvent("reopen-manage-subscription"));
              dispatch(closeCart());
              setTimeout(() => dispatch(resetCheckout()), 350);
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
            Back
          </BackBtn>
          <PrimaryBtn
            disabled={submitting}
            $dimmed={submitting}
            onClick={async () => {
              if (submitting) return;
              // Gate on consent for a real upgrade. Shake whichever box is
              // missing rather than silently doing nothing — same feedback the
              // subscription signup gives.
              if (!isDowngrade && hasChanges && !(termsAccepted && waiverAccepted)) {
                setConsentAttempted(true);
                if (!termsAccepted) { setShakeTerms(true); setTimeout(() => setShakeTerms(false), 500); }
                if (!waiverAccepted) { setShakeWaiver(true); setTimeout(() => setShakeWaiver(false), 500); }
                return;
              }
              setSubmitting(true);
              setError(null);
              setCardError(false);
              setAuthError(false);
              try {
                if (!idemKeyRef.current) idemKeyRef.current = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
                const resp = await instance.post("/stripe/subscriptions/update", {
                  channels: newChannels,
                  claims: newClaims,
                  idempotencyKey: idemKeyRef.current,
                  // Sent as captured, never hard-coded — the whole point of the
                  // boxes above is that the record reflects what the user did.
                  termsAccepted,
                  waiverAccepted,
                });
                const isUpgrade = resp?.data?.isUpgrade;
                const orderId = resp?.data?.orderId || null;
                const upgradeInvoiceId = resp?.data?.upgradeInvoiceId || null;

                // Change accepted — switch the button to its "Finalizing..."
                // stage. For an upgrade the order is built by the invoice.paid
                // webhook BEHIND THE SCENES; we keep the button spinning while
                // we poll for it, then navigate to a fully-rendered success
                // page (no on-page loading). A downgrade charges nothing.
                setFinalizing(true);

                if (!isUpgrade) {
                  // Downgrade: deferred to next renewal, no order → subscription page.
                  dispatch(closeCart());
                  setTimeout(() => dispatch(resetCheckout()), 350);
                  navigate(ERoutes.AccountSubscription);
                  return;
                }

                // Upgrade: wait (on the button) for the webhook-built order.
                const order = orderId
                  ? await fetchOrder(orderId)               // inline-built ($0 / dev) — already there
                  : upgradeInvoiceId
                    ? await pollUpgradeOrder(upgradeInvoiceId)
                    : null;

                dispatch(closeCart());
                setTimeout(() => dispatch(resetCheckout()), 350);
                if (order) {
                  navigate(`${ERoutes.OrderSuccess}/${order.id || order._id}`, { state: { order } });
                } else if (upgradeInvoiceId) {
                  // Webhook didn't land within the wait — the honest "being
                  // processed" page; the reconcile backstop finishes it within minutes.
                  navigate(`${ERoutes.OrderSuccess}?upgradeInvoice=${encodeURIComponent(upgradeInvoiceId)}`);
                } else {
                  navigate(ERoutes.AccountSubscription);
                }
              } catch (err: any) {
                const status = err?.response?.status;
                const raw = String(err?.response?.data?.message || "");
                // "No token provided" is the AUTH error (session expired), not a
                // card problem — route it to a log-in prompt. Card declines get
                // the card message; everything else falls back to its own text.
                const isAuth = status === 401 || /no token|unauthor|not allowed/i.test(raw);
                const isCard = !isAuth && /card|declined|payment method|charge/i.test(raw);
                setCardError(isCard);
                setAuthError(isAuth);
                setError(
                  isAuth
                    ? "Your session has expired — please log in again to continue."
                    : isCard
                      ? "Please verify your card above."
                      : (raw || "Something went wrong. Please try again."),
                );
                setSubmitting(false);
                setFinalizing(false);
              }
            }}
          >
            {finalizing
              ? <><Spinner />Finalizing...</>
              : submitting
                ? <><Spinner />Processing...</>
                : isDowngrade
                  ? "Confirm Downgrade"
                  : isAnnual
                    ? `Pay $${proratedDiffAnnual.toFixed(2)} Now`
                    : `Pay $${chargeTodayMonthly.toFixed(2)} Now`}
          </PrimaryBtn>
        </Actions>
      </SlidePanelFooter>
    </>
  );
};

export default UpgradeReviewView;
